import { useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import IntentService, { errorMessage } from 'src/services/IntentService';
import { attachStream, streamProfile } from 'src/services/FlowWorkspaceService';
import EventService from 'src/services/EventService';
import ServiceSpecializationPicker, {
    serviceSpecializationLabels,
    serviceSpecializationCodesForCategory,
    validServiceSpecializationCodes,
} from 'src/components/ServiceSpecializationPicker';

import { safeExternalUrl } from 'src/utils/intentEconomics';
import { getLeadStreamFilterProfileSummary } from 'src/utils/leadStreamFilterProfile';

import { StreamSection } from './StreamPageSections';
import StreamChangesDialog from './StreamChangesDialog';
import StreamAiTraining from './StreamAiTraining';
import { confirmedStreamDetails, streamDetailsChanges } from './streamDetailsChanges';

import type { Profile, Workspace } from 'src/services/IntentService';
import type { Stream } from 'src/services/LeadStreamsService/agent';

export default function StreamWorkspaceSections({ stream, onUpdated, showTraining }: { stream: Stream; onUpdated: (stream: Stream) => void; showTraining: boolean }) {
    const [workspace, setWorkspace] = useState<Workspace | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        let live = true;
        setLoading(true);
        setError('');
        setWorkspace(null);
        IntentService.list()
            .then(({ workspaces }) => {
                if (live)
                    setWorkspace(
                        workspaces.find((item) => item.streams.some((candidate) => candidate.id === stream.id)) || null
                    );
            })
            .catch((reason) => {
                if (live) setError(errorMessage(reason));
            })
            .finally(() => {
                if (live) setLoading(false);
            });
        return () => {
            live = false;
        };
    }, [stream.id, retry]);
    if (loading || error)
        return (
            <StreamSection
                id="rules"
                title="Настройка потока"
            >
                {loading ? (
                    <CircularProgress
                        size={22}
                        aria-label="Загрузка деталей потока"
                    />
                ) : (
                    <Alert
                        severity="error"
                        action={<Button onClick={() => setRetry((value) => value + 1)}>Повторить</Button>}
                    >
                        {error}
                    </Alert>
                )}
            </StreamSection>
        );
    return (
        <StreamDetails
            key={stream.id}
            stream={stream}
            workspace={workspace}
            onSaved={setWorkspace}
            onUpdated={onUpdated}
            showTraining={showTraining}
        />
    );
}

function StreamDetails({
    stream,
    workspace,
    onSaved,
    onUpdated,
    showTraining,
}: {
    stream: Stream;
    workspace: Workspace | null;
    onSaved: (workspace: Workspace) => void;
    onUpdated: (stream: Stream) => void;
    showTraining: boolean;
}) {
    const streamScope = stream.filter_profile || {};
    const streamCodesValid =
        streamScope.service_specialization_scope_declared === true &&
        streamScope.service_specialization_scope_invalid !== true &&
        (!streamScope.service_specialization_unknown_codes ||
            streamScope.service_specialization_unknown_codes.length === 0) &&
        validServiceSpecializationCodes(streamScope.service_specialization_codes) &&
        streamScope.service_specialization_codes.length > 0;
    const hasRawStreamScope =
        'service_specialization_codes' in streamScope ||
        'service_specialization_scope_declared' in streamScope ||
        'service_specialization_unknown_codes' in streamScope ||
        'service_specialization_scope_invalid' in streamScope;
    const invalidStreamScope = hasRawStreamScope && !streamCodesValid;
    const initial = { ...(workspace?.profile || streamProfile(stream)) };
    delete initial.service_specialization_codes;
    if (streamCodesValid) initial.service_specialization_codes = [...streamScope.service_specialization_codes!];
    const initialName = workspace?.name || stream.name || stream.category.name;
    const [profile, setProfile] = useState<Profile>(initial);
    const [specializationsTouched, setSpecializationsTouched] = useState(false);
    const [name, setName] = useState(initialName);
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [reviewing, setReviewing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [pending, setPending] = useState<Workspace>();
    const mounted = useRef(true);
    const saving = useRef(false);
    const editButton = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const dirty =
        specializationsTouched || JSON.stringify(profile) !== JSON.stringify(initial) || name.trim() !== initialName;
    const shared = (workspace?.streams.length || 0) > 1;
    const changes = streamDetailsChanges(stream, initialName, initial, name, profile, {
        specializationTouched: specializationsTouched,
        invalidSpecializationScope: invalidStreamScope,
    });
    useEffect(() => {
        if (!dirty) return;
        const prevent = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', prevent);
        return () => window.removeEventListener('beforeunload', prevent);
    }, [dirty]);
    const persist = async () => {
        if (saving.current || !reviewing || shared) return;
        if (!name.trim()) {
            setError('Укажите название потока.');
            return;
        }
        saving.current = true;
        setBusy(true);
        setError('');
        setSaved(false);
        try {
            const prepared =
                pending ||
                workspace ||
                (await attachStream(stream, pending, (value) => {
                    if (mounted.current) setPending(value);
                }));
            if (!mounted.current) return;
            if (prepared.streams.length !== 1 || prepared.streams[0].id !== stream.id) {
                throw new Error('shared-stream-profile');
            }
            if (mounted.current) setPending(prepared);
            const profilePatch = Object.fromEntries(
                Object.entries(profile).filter(([key, value]) =>
                    key === 'service_specialization_codes'
                        ? specializationsTouched
                        : JSON.stringify(value) !== JSON.stringify(initial[key as keyof Profile])
                )
            ) as Profile;
            const result = await IntentService.save(
                { ...prepared, name: name.trim(), profile },
                profilePatch
            );
            if (!confirmedStreamDetails(result, prepared, stream.id, name.trim(), profile)) {
                throw new Error('unconfirmed-stream-save');
            }
            window.dispatchEvent(new Event('intent-workspaces-updated'));
            EventService.dispatch('LEAD_STREAMS_UPDATED');
            if (!mounted.current) return;
            setName(result.name);
            setProfile(result.profile);
            setSpecializationsTouched(false);
            onSaved(result);
            setEditing(false);
            setReviewing(false);
            setPending(undefined);
            setSaved(true);
        } catch (reason) {
            if (mounted.current) {
                const message = reason instanceof Error ? reason.message : '';
                setError(
                    message === 'shared-stream-profile'
                        ? 'Эти детали относятся к нескольким потокам. Изменения не применены.'
                        : message === 'unconfirmed-stream-save'
                        ? 'Не удалось подтвердить сохранение. Обновите страницу и проверьте детали. Черновик оставлен в форме.'
                        : (reason as { response?: { status?: number } })?.response?.status === 409
                        ? 'Детали уже изменились в другой вкладке. Скопируйте черновик и обновите страницу.'
                        : errorMessage(reason)
                );
            }
        } finally {
            saving.current = false;
            if (mounted.current) setBusy(false);
        }
    };
    const field = (
        id: string,
        label: string,
        value: string,
        onChange: (value: string) => void,
        multiline = false,
        maxLength = 2000
    ) => (
        <Box>
            <Typography
                component="label"
                htmlFor={id}
                sx={{ display: 'block', mb: 0.75, fontSize: 13, fontWeight: 600, color: 'var(--lm-muted)' }}
            >
                {label}
            </Typography>
            <TextField
                id={id}
                value={value}
                fullWidth
                disabled={busy}
                onChange={(event) => onChange(event.target.value)}
                multiline={multiline}
                InputProps={multiline ? { inputComponent: 'textarea' } : undefined}
                inputProps={{ maxLength, ...(multiline ? { rows: id === 'stream-description' ? 3 : 2 } : {}) }}
                sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'var(--lm-surface)' },
                    '& textarea': {
                        resize: 'vertical',
                        padding: '12px 14px',
                        minHeight: id === 'stream-description' ? 90 : 64,
                    },
                }}
            />
        </Box>
    );
    const profileField = (key: keyof Profile, label: string, multiline = false, maxLength = 2000) =>
        field(
            `stream-${key}`,
            label,
            String(profile[key] || ''),
            (value) => setProfile((current) => ({ ...current, [key]: value })),
            multiline,
            maxLength
        );
    const legacyKnowledge = [initial.knowledge_notes, initial.website_notes].filter(Boolean).join('\n\n');
    const rules = getLeadStreamFilterProfileSummary(stream);
    const detailRows: { label: string; value: string }[] = [];
    const shownValues = new Set<string>();
    const addDetail = (label: string, value?: string) => {
        const trimmed = value?.trim();
        if (!trimmed) return;
        const normalized = trimmed.toLocaleLowerCase('ru').replace(/\s+/g, ' ');
        if (shownValues.has(normalized)) return;
        shownValues.add(normalized);
        detailRows.push({ label, value: trimmed });
    };
    addDetail('Ищем', rules.offer || stream.category.name);
    addDetail('Название', initialName);
    addDetail('Категория', stream.category.name);
    addDetail('Гео отбора', rules.geo || initial.region);
    if (rules.geo && initial.region) addDetail('Локация в описании', initial.region);
    addDetail('Задача лидогенерации', initial.description);
    addDetail('Описание бизнеса', initial.business_description);
    addDetail('Сайт', initial.website);
    const specializationLabels = validServiceSpecializationCodes(initial.service_specialization_codes)
        ? serviceSpecializationLabels(initial.service_specialization_codes)
        : [];
    addDetail('Специализации', specializationLabels.join(', '));
    const uniqueRules = (items: string[]) => {
        const seen = new Set<string>();
        return items
            .map((item) => item.trim())
            .filter((item) => {
                const normalized = item.toLocaleLowerCase('ru').replace(/\s+/g, ' ');
                if (!normalized || seen.has(normalized)) return false;
                seen.add(normalized);
                return true;
            });
    };
    const targetRules = uniqueRules(rules.targetIntents);
    const excludedRules = uniqueRules(rules.exclude);
    return (
        <StreamSection
            id="rules"
            title="Настройка потока"
            summary={dirty ? 'Есть изменения' : rules.offer || initialName}
        >
            <Prompt
                when={dirty && !busy}
                message="Есть несохранённые изменения потока. Перейти без сохранения?"
            />
            <Stack spacing={2.5}>
                {editing ? (
                    <Box
                        component="form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!name.trim()) setError('Укажите название потока.');
                            else if (dirty) {
                                setError('');
                                setReviewing(true);
                            }
                        }}
                    >
                        <Stack spacing={2}>
                            {field('stream-name', 'Название потока', name, setName, false, 120)}
                            {profileField('description', 'Задача лидогенерации', true)}
                            {profileField('business_description', 'Описание бизнеса', true)}
                            {invalidStreamScope && (
                                <Alert
                                    severity="warning"
                                    action={
                                        <Button
                                            color="inherit"
                                            disabled={busy}
                                            onClick={() => {
                                                setSpecializationsTouched(true);
                                                setProfile((current) => ({
                                                    ...current,
                                                    service_specialization_codes: [],
                                                }));
                                            }}
                                        >
                                            Сбросить специализации
                                        </Button>
                                    }
                                >
                                    В потоке сохранена прежняя или некорректная настройка специализаций. Сбросьте её,
                                    чтобы отбор снова опирался на описание бизнеса.
                                </Alert>
                            )}
                            {(serviceSpecializationCodesForCategory(stream.category.id).length > 0 ||
                                validServiceSpecializationCodes(profile.service_specialization_codes) ||
                                invalidStreamScope) && (
                                <ServiceSpecializationPicker
                                    id="stream-service-specializations"
                                    categoryId={stream.category.id}
                                    value={profile.service_specialization_codes || []}
                                    disabled={busy}
                                    onChange={(service_specialization_codes) => {
                                        setSpecializationsTouched(true);
                                        setProfile((current) => ({ ...current, service_specialization_codes }));
                                    }}
                                />
                            )}
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                {profileField('region', 'Локация', false, 200)}
                                {profileField('website', 'Сайт', false, 1000)}
                            </Box>
                            <Typography sx={{ fontSize: 13, color: 'var(--lm-muted)' }}>
                                ИИ уточняет отбор по вашим оценкам лидов ниже.
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={busy || !dirty}
                                >
                                    Проверить изменения
                                </Button>
                                <Button
                                    disabled={busy}
                                    onClick={() => {
                                        setProfile(initial);
                                        setName(initialName);
                                        setSpecializationsTouched(false);
                                        setEditing(false);
                                        setError('');
                                    }}
                                >
                                    Отмена
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                ) : (
                    <>
                        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {detailRows.map(({ label, value }) => (
                                <Box
                                    key={label}
                                    sx={{
                                        minWidth: 0,
                                            gridColumn:
                                                label === 'Задача лидогенерации' ||
                                                label === 'Описание бизнеса' ||
                                                label === 'Специализации'
                                                    ? '1 / -1'
                                                    : undefined,
                                    }}
                                >
                                    <Typography
                                        component="dt"
                                        sx={{ fontSize: 13, color: 'var(--lm-muted)', mb: 0.5 }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography
                                        component="dd"
                                        sx={{
                                            m: 0,
                                            fontSize: 15,
                                            fontWeight: label === 'Ищем' || label === 'Название' ? 600 : 400,
                                            whiteSpace: 'pre-wrap',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {label === 'Сайт' && safeExternalUrl(value) ? (
                                            <Box
                                                component="a"
                                                href={safeExternalUrl(value)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: 'var(--lm-accent-ink)', fontFamily: 'inherit' }}
                                            >
                                                {value} ↗
                                            </Box>
                                        ) : (
                                            value
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box>
                            <Button
                                ref={editButton}
                                variant="outlined"
                                disabled={shared}
                                onClick={() => {
                                    setEditing(true);
                                    setSaved(false);
                                }}
                            >
                                Изменить
                            </Button>
                            {shared && (
                                <Typography sx={{ mt: 1, fontSize: 13, color: 'var(--lm-muted)' }}>
                                    Эти детали общие для нескольких потоков. Отдельное редактирование пока недоступно.
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ borderTop: '1px solid var(--lm-border)', pt: 2 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>Обучение ИИ на ваших оценках</Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--lm-muted)', mb: 1.5 }}>
                                Оценивайте лиды как подходящие или неподходящие. ИИ учитывает ваши оценки и улучшает качество поиска лидов.
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: 'var(--lm-text)', mb: targetRules.length || excludedRules.length ? 2 : 0 }}>
                                Примеры: <Box component="strong" sx={{ color: 'var(--lm-success-ink)' }}>{rules.positiveExamplesCount} подходит</Box>,{' '}
                                <Box component="strong">{rules.negativeExamplesCount} не подходит</Box> · Оценок: <Box component="strong">{rules.feedbackCount}</Box>
                            </Typography>
                            {(targetRules.length > 0 || excludedRules.length > 0) && (
                                <>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Как ИИ отбирает лиды</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                {[
                                    { label: 'Засчитываем', items: targetRules },
                                    { label: 'Исключаем', items: excludedRules },
                                ].filter(({ items }) => items.length > 0).map(({ label, items }) => (
                                    <Box key={label}>
                                        <Typography sx={{ fontSize: 13, color: 'var(--lm-muted)', mb: 1 }}>{label}</Typography>
                                        <Stack spacing={0.6}>
                                            {items.map((item) => (
                                                <Typography key={item} sx={{ fontSize: 14, lineHeight: 1.4, color: label === 'Засчитываем' ? 'var(--lm-accent-ink)' : 'var(--lm-text)' }}>
                                                    {item}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                                </Box>
                                </>
                            )}
                        </Box>
                    </>
                )}
                {error && !reviewing && <Alert severity="error">{error}</Alert>}
                {saved && (
                    <Typography
                        role="status"
                        variant="body2"
                        color="text.secondary"
                    >
                        Настройки потока сохранены. Автопополнение отключено.
                    </Typography>
                )}
                {legacyKnowledge && (
                    <Box
                        component="details"
                        sx={{ borderTop: '1px solid var(--lm-border)', pt: 2 }}
                    >
                        <Box
                            component="summary"
                            sx={{ cursor: 'pointer', color: 'var(--lm-muted)', fontSize: 14 }}
                        >
                            Ранее сохранённые материалы
                        </Box>
                        <Stack
                            spacing={1.5}
                            sx={{ pt: 2 }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Материалы сохранены. Их можно скопировать в базу знаний ИИ-менеджера.
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    maxHeight: 240,
                                    overflow: 'auto',
                                }}
                            >
                                {legacyKnowledge}
                            </Typography>
                            <Box>
                                <Button
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(legacyKnowledge);
                                            setCopied(true);
                                        } catch {
                                            setError('Не удалось скопировать. Выделите и скопируйте текст вручную.');
                                        }
                                    }}
                                >
                                    {copied ? 'Скопировано' : 'Скопировать материалы'}
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                )}
                {showTraining && <StreamAiTraining stream={stream} />}
            </Stack>
            <StreamChangesDialog
                open={reviewing}
                changes={changes}
                busy={busy}
                error={error}
                onBack={() => {
                    setReviewing(false);
                    setError('');
                }}
                onConfirm={() => {
                    void persist();
                }}
                onExited={() => {
                    if (saved) editButton.current?.focus();
                }}
            />
        </StreamSection>
    );
}
